import { Box, Drawer, Link, Typography } from "@mui/material";
import { ReactElement } from "react";
import { CancelOutlined } from "@mui/icons-material";

type Props = {
    selectedPublication: Record<string, any>;
    isDrawerOpen: boolean;
    handleCloseDrawer: (value: boolean) => void;
}

const PublicationDetails = (props: Props) => {
    const { selectedPublication, isDrawerOpen, handleCloseDrawer } = props;

    const handleClose = () => {
        handleCloseDrawer(false);
    };

    const domainsKeyValues: Record<string, string> = {
        hbcdDomains: 'HBCD Domains',
        abcdDomains: 'ABCD Domains',
        hbcdReleaseVersion: 'HBCD Release Version',
        abcdReleaseVersion: 'ABCD Release Version',
    }

    const publicationKeyValue: Record<string, any> = {
        datePublished: 'Date Published',
        additionalFields: {
            title: 'Domains',
            formatter: (value: string) => {
                const additionalFields = JSON.parse(value || '{}');
                return Object.entries(additionalFields).map(([fieldKey, fieldValue]: any) => {
                    const parsedFieldValue = fieldValue.replaceAll('[', '').replaceAll(']', '');
                    return (
                        <Typography key={fieldKey} variant="body2">
                            {domainsKeyValues[fieldKey]}: {parsedFieldValue}
                        </Typography>
                    );
                })
            },
        },
        study: {
            title: 'Studies', formatter: (value: any) => JSON.parse(value).toString().toUpperCase(),
        },
        doi: "DOI URL"
    };

    function getTitle(key: string): string {
        const titleType = typeof publicationKeyValue[key]
        switch (titleType) {
        case "string":
            return publicationKeyValue[key];
        case "object":
            return publicationKeyValue[key].title
        default:
            return key.charAt(0).toUpperCase() + key.slice(1)
        }
    }

    function getText(key: string, value: string | number): string | number | undefined {
        switch (typeof publicationKeyValue[key]) {
        case "object":
            return publicationKeyValue[key].formatter(value)
        case 'string':
            return typeof value == 'string' && value?.trim()?.length > 0
                ? value
                : "N/A";
        default:
            if (typeof value == 'string') return value?.trim()?.length > 0
                ? value
                : "N/A"
            return value
        }
    }

    function PublicationText({ text, title }: { text: string | number | undefined, title: string }): ReactElement {
        if (title === 'DOI URL' && typeof text === 'string' && text !== 'N/A') {
            return (
                <Link sx={{ cursor: "pointer" }} target="_blank" rel="noopener" href={text} color="inherit">
                    {text}
                </Link>
            )
        }

        return <>
            {
                typeof text === 'string' ? (
                    <Typography variant="body1">{text}</Typography>
                ) : (
                    <Box>{text}</Box>
                )
            }
        </>
    }

    function Details({ selectedPublication }: { selectedPublication: Record<string, any> }): ReactElement {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {selectedPublication &&
                    Object.entries(selectedPublication).map(([key, value]) => {
                        if (key === 'id' || key === 'owner') return;
                        const title = getTitle(key);
                        const text = getText(key, value);
                        return (
                            <Box key={key} sx={{ marginBottom: 3 }}>
                                <Typography fontWeight={700} variant="body1">
                                    {title}
                                </Typography>
                                <PublicationText text={text} title={title}/>
                            </Box>
                        )
                    })}
            </Box>
        )
    }

    return (
        <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={handleClose}
            sx={{ width: 600 }}
        >
            <Box sx={{ paddingX: '32px', paddingY: '48px', width: 600 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                }}>
                    <Typography variant="h5">
                        Publication details
                    </Typography>
                    <CancelOutlined sx={{ cursor: 'pointer' }} onClick={handleClose}/>
                </Box>
                <Details selectedPublication={selectedPublication}/>
            </Box>
        </Drawer>
    );
}

export default PublicationDetails;